import React, { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { 
  Save, 
  Play, 
  Trash2, 
  Plus, 
  Clock, 
  Settings, 
  Database,
  FileText,
  GraduationCap,
  MessageSquare,
  X
} from 'lucide-react';

const MockDataManager = ({ onClose, onApplyMockData, selectedStudentId = null }) => {
  const { currentUser, family } = useFamilyContext();
  const [activeTab, setActiveTab] = useState('preferences');
  const [mockPreferences, setMockPreferences] = useState([]);
  const [mockResponses, setMockResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDataName, setNewDataName] = useState('');
  const [newDataDescription, setNewDataDescription] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    loadMockData();
  }, [currentUser]);

  const loadMockData = async () => {
    if (!currentUser) return;
    
    try {
      // FIXED: Use prop first, then localStorage, then current user ID
      const testingProfileId = localStorage.getItem('testing-profile-id');
      const effectiveStudentId = selectedStudentId || testingProfileId || currentUser.id;
      
      // CRITICAL DEBUG: Log to confirm this new code is running
      console.log('🚨 UPDATED MOCK DATA MANAGER RUNNING 🚨');
      
      console.log('🔍 MOCK DATA MANAGER: Loading data for testing profile:', {
        'currentUser.id': currentUser.id,
        'currentUser.name': currentUser.name,
        'prop.selectedStudentId': selectedStudentId,
        'testingProfileId': testingProfileId,
        'effectiveStudentId': effectiveStudentId,
        'isParentView': !!testingProfileId || !!selectedStudentId
      });
      
      // FIXED: Use effective student ID for queries
      // Load mock college preferences - filter by effective student_id
      console.log('🔍 QUERYING MOCK PREFERENCES with student_id:', effectiveStudentId);
      
      const { data: preferencesData, error: preferencesError } = await supabase
        .from('mock_college_preferences')
        .select('*')
        .eq('student_id', effectiveStudentId)
        .order('created_at', { ascending: false });
      
      if (preferencesError) {
        console.error('❌ ERROR loading mock preferences:', preferencesError);
        console.error('❌ Query was: student_id =', effectiveStudentId);
      } else {
        console.log('✅ MOCK PREFERENCES FOUND:', preferencesData?.length || 0);
        if (preferencesData && preferencesData.length > 0) {
          console.log('✅ PREFERENCES DATA:', preferencesData);
        } else {
          console.log('⚠️ NO PREFERENCES FOUND - trying debug query...');
          
          // Debug query to see what's actually in the table
          const { data: allPrefs, error: debugError } = await supabase
            .from('mock_college_preferences')
            .select('id, name, student_id, created_by')
            .limit(10);
            
          if (debugError) {
            console.error('❌ DEBUG QUERY ERROR:', debugError);
          } else {
            console.log('🔍 ALL PREFERENCES IN TABLE (first 10):', allPrefs);
            console.log('🔍 LOOKING FOR student_id:', effectiveStudentId);
            
            // Also check what family member records exist for RLS debugging
            const { data: familyMembers, error: familyError } = await supabase
              .from('family_members')
              .select('id, auth_user_id, name')
              .eq('auth_user_id', currentUser.auth_user_id || currentUser.authUserId);
              
            if (familyError) {
              console.error('❌ FAMILY MEMBERS QUERY ERROR:', familyError);
            } else {
              console.log('🔍 FAMILY MEMBERS FOR CURRENT AUTH USER:', familyMembers);
            }
          }
        }
        setMockPreferences(preferencesData || []);
      }
      
      // Load mock question responses - filter by selected student_id
      const { data: responsesData, error: responsesError } = await supabase
        .from('mock_question_responses')
        .select('*')
        .eq('student_id', effectiveStudentId)
        .order('created_at', { ascending: false });
      
      if (responsesError) {
        console.error('Error loading mock responses:', responsesError);
      } else {
        console.log('✅ MOCK RESPONSES FOUND:', responsesData?.length || 0);
        setMockResponses(responsesData || []);
      }
    } catch (error) {
      console.error('Error loading mock data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveMockPreferences = async () => {
    if (!newDataName.trim()) return;
    
    setSaving(true);
    try {
      // Get current college preferences from localStorage or context
      const currentPreferences = JSON.parse(localStorage.getItem('mock-college-preferences') || '{}');
      const authUserId = currentUser.auth_user_id || currentUser.authUserId;
      
      // FIXED: Use effective student ID (prop first, then localStorage, then current user)
      const testingProfileId = localStorage.getItem('testing-profile-id');
      const effectiveStudentIdForSave = selectedStudentId || testingProfileId || currentUser.id;
      
      const { error } = await supabase
        .from('mock_college_preferences')
        .insert({
          name: newDataName.trim(),
          description: newDataDescription.trim(),
          preferences: currentPreferences,
          created_by: authUserId,
          student_id: effectiveStudentIdForSave
        });
      
      if (error) {
        console.error('Error saving mock preferences:', error);
        alert('Error saving mock preferences');
      } else {
        setNewDataName('');
        setNewDataDescription('');
        setShowCreateForm(false);
        loadMockData();
        alert('Mock preferences saved successfully!');
      }
    } catch (error) {
      console.error('Error saving mock preferences:', error);
      alert('Error saving mock preferences');
    } finally {
      setSaving(false);
    }
  };

  const saveMockResponses = async () => {
    if (!newDataName.trim()) return;
    
    setSaving(true);
    try {
      // Get current assessment responses from recent assessment_responses table
      const { data: recentAssessment, error: assessmentError } = await supabase
        .from('assessment_responses')
        .select('responses, assessment_type')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (assessmentError) {
        console.error('Error loading recent assessment:', assessmentError);
        alert('No recent assessment found to save');
        return;
      }
      
      if (!recentAssessment || recentAssessment.length === 0) {
        alert('No recent assessment found to save');
        return;
      }
      
      const authUserId = currentUser.auth_user_id || currentUser.authUserId;
      
      // FIXED: Use effective student ID (prop first, then localStorage, then current user)
      const testingProfileId = localStorage.getItem('testing-profile-id');
      const effectiveStudentIdForSave = selectedStudentId || testingProfileId || currentUser.id;
      
      const { error } = await supabase
        .from('mock_question_responses')
        .insert({
          name: newDataName.trim(),
          description: newDataDescription.trim(),
          responses: recentAssessment[0].responses,
          assessment_type: recentAssessment[0].assessment_type,
          created_by: authUserId,
          student_id: effectiveStudentIdForSave // Set the student_id to the effective student ID
        });
      
      if (error) {
        console.error('Error saving mock responses:', error);
        alert('Error saving mock responses');
      } else {
        setNewDataName('');
        setNewDataDescription('');
        setShowCreateForm(false);
        loadMockData();
        alert('Mock responses saved successfully!');
      }
    } catch (error) {
      console.error('Error saving mock responses:', error);
      alert('Error saving mock responses');
    } finally {
      setSaving(false);
    }
  };

  const applyMockPreferences = async (mockData) => {
    try {
      // Store in localStorage for immediate use
      localStorage.setItem('mock-college-preferences', JSON.stringify(mockData.preferences));
      
      // Also update the student_academic_profiles table if needed
      const { error } = await supabase
        .from('student_academic_profiles')
        .upsert({
          user_id: currentUser.id,
          college_preferences: mockData.preferences,
          updated_at: new Date().toISOString()
        });
      
      if (error) {
        console.error('Error updating student profile:', error);
      }
      
      onApplyMockData?.('preferences', mockData);
      alert(`Applied mock preferences: ${mockData.name}`);
    } catch (error) {
      console.error('Error applying mock preferences:', error);
      alert('Error applying mock preferences');
    }
  };

  const applyMockResponses = async (mockData) => {
    try {
      // Create a new assessment_responses entry with the mock data
      const { error } = await supabase
        .from('assessment_responses')
        .insert({
          user_id: currentUser.id,
          assessment_type: mockData.assessment_type,
          responses: mockData.responses,
          insights: null, // Will be generated when running the assessment
          created_at: new Date().toISOString()
        });
      
      if (error) {
        console.error('Error applying mock responses:', error);
        alert('Error applying mock responses');
      } else {
        onApplyMockData?.('responses', mockData);
        alert(`Applied mock responses: ${mockData.name}`);
      }
    } catch (error) {
      console.error('Error applying mock responses:', error);
      alert('Error applying mock responses');
    }
  };

  const deleteMockData = async (type, id) => {
    if (!confirm('Are you sure you want to delete this mock data?')) return;
    
    try {
      const table = type === 'preferences' ? 'mock_college_preferences' : 'mock_question_responses';
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting mock data:', error);
        alert('Error deleting mock data');
      } else {
        loadMockData();
      }
    } catch (error) {
      console.error('Error deleting mock data:', error);
      alert('Error deleting mock data');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading mock data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6 text-azure-600" />
            <h2 className="text-2xl font-bold text-slate-900">Mock Data Manager</h2>
          </div>
          <button
            onClick={onClose}
            className="text-stone-500 hover:text-slate-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-200">
          <button
            onClick={() => setActiveTab('preferences')}
            className={`flex-1 px-6 py-4 text-sm font-medium ${
              activeTab === 'preferences'
                ? 'border-b-2 border-azure-600 text-azure-600'
                : 'text-stone-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <GraduationCap className="w-4 h-4" />
              College Preferences ({mockPreferences.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('responses')}
            className={`flex-1 px-6 py-4 text-sm font-medium ${
              activeTab === 'responses'
                ? 'border-b-2 border-azure-600 text-azure-600'
                : 'text-stone-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Question Responses ({mockResponses.length})
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Create New Form */}
          {showCreateForm && (
            <div className="mb-6 p-4 bg-stone-50 rounded-lg border">
              <h3 className="text-lg font-semibold mb-4">
                Save Current {activeTab === 'preferences' ? 'College Preferences' : 'Question Responses'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newDataName}
                    onChange={(e) => setNewDataName(e.target.value)}
                    placeholder="e.g., Sheppard Engineering Profile"
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={newDataDescription}
                    onChange={(e) => setNewDataDescription(e.target.value)}
                    placeholder="Brief description of this mock data set"
                    rows={2}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={activeTab === 'preferences' ? saveMockPreferences : saveMockResponses}
                    disabled={saving || !newDataName.trim()}
                    className="px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 bg-stone-300 text-slate-700 rounded-md hover:bg-stone-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Save Current {activeTab === 'preferences' ? 'Preferences' : 'Responses'}
            </button>
          </div>

          {/* Mock Data Lists */}
          {activeTab === 'preferences' && (
            <div className="space-y-4">
              {mockPreferences.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 text-stone-400" />
                  <p>No saved college preferences yet</p>
                  <p className="text-sm">Save your current preferences to reuse them later</p>
                </div>
              ) : (
                mockPreferences.map((mockData) => (
                  <div key={mockData.id} className="border border-stone-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{mockData.name}</h3>
                        {mockData.description && (
                          <p className="text-sm text-slate-600 mt-1">{mockData.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-stone-500">
                          <Clock className="w-3 h-3" />
                          {formatDate(mockData.created_at)}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => applyMockPreferences(mockData)}
                          className="px-3 py-1 bg-azure-600 text-white text-sm rounded hover:bg-azure-700 flex items-center gap-1"
                        >
                          <Play className="w-3 h-3" />
                          Apply
                        </button>
                        <button
                          onClick={() => deleteMockData('preferences', mockData.id)}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'responses' && (
            <div className="space-y-4">
              {mockResponses.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-stone-400" />
                  <p>No saved question responses yet</p>
                  <p className="text-sm">Complete an assessment to save responses for testing</p>
                </div>
              ) : (
                mockResponses.map((mockData) => (
                  <div key={mockData.id} className="border border-stone-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{mockData.name}</h3>
                        {mockData.description && (
                          <p className="text-sm text-slate-600 mt-1">{mockData.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(mockData.created_at)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Settings className="w-3 h-3" />
                            {mockData.assessment_type}
                          </div>
                          <div className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {Object.keys(mockData.responses).length} responses
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => applyMockResponses(mockData)}
                          className="px-3 py-1 bg-azure-600 text-white text-sm rounded hover:bg-azure-700 flex items-center gap-1"
                        >
                          <Play className="w-3 h-3" />
                          Apply
                        </button>
                        <button
                          onClick={() => deleteMockData('responses', mockData.id)}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MockDataManager;